/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-09
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStudioPermissions } from '../../hooks/useStudioPermissions';
import { StudioRole } from '../../types/studio';

/**
 * Props for the UsageIndicator component
 */
interface UsageIndicatorProps {
  /**
   * Type of usage being displayed
   */
  type: 'studios' | 'members';
  /**
   * Current usage count
   */
  current: number;
  /**
   * Maximum allowed by plan (0 = unlimited)
   */
  limit: number;
  /**
   * Label for the usage type
   */
  label?: string;
  /**
   * Show as compact version
   */
  compact?: boolean;
  /**
   * Show progress bar
   */
  showProgress?: boolean;
  /**
   * Optional click handler for upgrade action
   */
  onUpgrade?: () => void;
  /**
   * Custom styling
   */
  sx?: object;
}

/**
 * Component to display subscription usage indicators with visual progress
 * Shows current usage vs plan limits with contextual warnings and upgrade prompts
 */
const UsageIndicator: React.FC<UsageIndicatorProps> = ({
  type,
  current,
  limit,
  label,
  compact = false,
  showProgress = false,
  onUpgrade,
  sx = {},
}) => {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const { role } = useStudioPermissions();
  const isStudioOwner = role === StudioRole.OWNER;

  // Handle unlimited plans
  const isUnlimited = limit === 0;
  const percentage = isUnlimited ? 0 : Math.min(100, (current / limit) * 100);

  // Determine warning level
  const getWarningLevel = () => {
    if (isUnlimited) return 'none';
    if (percentage >= 100) return 'error';
    if (percentage >= 95) return 'error';
    if (percentage >= 80) return 'warning';
    return 'none';
  };

  const warningLevel = getWarningLevel();

  // Get appropriate colors and icons
  const getStatusColor = () => {
    switch (warningLevel) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = () => {
    switch (warningLevel) {
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'warning':
        return <WarningIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Handle upgrade click
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/pricing', {
        state: {
          upgradeContext: `${type}_limit`,
          currentUsage: { [type]: current },
        },
      });
    }
  };

  // Format display text
  const displayLabel = label || t(`usage.${type}`);
  const usageText = isUnlimited
    ? `${current} ${displayLabel.toLowerCase()}`
    : `${current} / ${limit} ${displayLabel.toLowerCase()}`;

  // Get tooltip message (only for owners or no warning)
  const getTooltipMessage = () => {
    if (!isStudioOwner) {
      return `${current} ${displayLabel.toLowerCase()}`;
    }
    if (isUnlimited) {
      return t('usage.unlimitedTooltip', {
        resource: displayLabel.toLowerCase(),
      });
    }
    if (percentage >= 100) {
      return t('usage.atLimitTooltip', {
        resource: displayLabel.toLowerCase(),
      });
    }
    if (percentage >= 95) {
      return t('usage.nearLimitTooltip', {
        resource: displayLabel.toLowerCase(),
      });
    }
    if (percentage >= 80) {
      return t('usage.approachingLimitTooltip', {
        resource: displayLabel.toLowerCase(),
        percent: percentage.toFixed(0),
      });
    }
    return t('usage.normalUsageTooltip', {
      current,
      limit,
      resource: displayLabel.toLowerCase(),
    });
  };

  // Compact version for navigation bars
  if (compact) {
    return (
      <Tooltip title={getTooltipMessage()}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor:
              warningLevel !== 'none' && isStudioOwner ? 'pointer' : 'default',
            ...sx,
          }}
          onClick={
            warningLevel !== 'none' && isStudioOwner ? handleUpgrade : undefined
          }
        >
          {isStudioOwner && getStatusIcon()}
          <Typography
            variant="caption"
            sx={{
              color:
                warningLevel !== 'none' && isStudioOwner
                  ? `${getStatusColor()}.main`
                  : 'text.secondary',
              fontWeight: warningLevel !== 'none' && isStudioOwner ? 600 : 400,
            }}
          >
            {isStudioOwner
              ? usageText
              : `${current} ${displayLabel.toLowerCase()}`}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // Full version with optional progress bar
  return (
    <Box sx={{ ...sx }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: showProgress && isStudioOwner ? 1 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isStudioOwner && getStatusIcon()}
          <Typography variant="body2" fontWeight={500}>
            {displayLabel}
          </Typography>
          <Chip
            label={
              isStudioOwner
                ? usageText
                : `${current} ${displayLabel.toLowerCase()}`
            }
            size="small"
            color={isStudioOwner ? getStatusColor() : 'primary'}
            variant={
              warningLevel !== 'none' && isStudioOwner ? 'filled' : 'outlined'
            }
          />
        </Box>

        {warningLevel !== 'none' && isStudioOwner && (
          <Button
            size="small"
            variant="outlined"
            color={getStatusColor()}
            startIcon={<TrendingUpIcon />}
            onClick={handleUpgrade}
            sx={{ minWidth: 'auto' }}
          >
            {t('usage.upgradeNow')}
          </Button>
        )}
      </Box>

      {showProgress && !isUnlimited && isStudioOwner && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={percentage}
            color={getStatusColor()}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block' }}
          >
            {t('usage.percentageUsed', { percent: percentage.toFixed(0) })}
          </Typography>
        </Box>
      )}

      {warningLevel !== 'none' && isStudioOwner && (
        <Typography
          variant="caption"
          color={`${getStatusColor()}.main`}
          sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}
        >
          {getTooltipMessage()}
        </Typography>
      )}
    </Box>
  );
};

export default UsageIndicator;
